import { inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Functions } from "@angular/fire/functions";
import { Store } from "@ngxs/store";
import { httpsCallable } from "firebase/functions";
import html2pdf from "node_modules/html2pdf.js/src/index.js";
import Quill, { DeltaStatic } from "quill";
import {
  cloudFunctionNames,
  CoverLetterType,
  ICoverLetterData,
  ICustomCoverLetterContent,
  IDeprecatedEntity,
  IEntity,
  UUID,
} from "src/app/core/interfaces";
import {
  coverLetterDefaults,
  coverLetterFooter,
  coverLetterHeader,
  getLogo,
  getLogoAndAddressHtml,
} from "src/app/modules/settings/components/billing/cover-letter-defaults";
import { SecureProStateModel } from "src/app/state/secure-pro-state/secure-pro-model.interface";
import {
  ICreateDeprecatedReportReq,
  ICreateDeprecatedReportRes,
  ICreateReportReq,
  ICreateReportRes,
  ICreateSnapshotReportReq,
  ICreateSnapshotReportRes
} from "@cloud-functions/interfaces";
import { UserState } from "../../state/user-state/user.state";

export interface IHTML2PDFOptions {
  margin?: number;
  filename?: string;
  image?: { type: string; quality: number };
  pagebreak?: { mode: "css" | "legacy" };
  html2canvas?: {
    scale: number;
    useCORS: boolean;
  };
  enableLinks?: boolean;
  jsPDF?: { unit: string; format: string; orientation: string };
}

@Injectable({
  providedIn: "root",
})
export class PdfService {
  private functions: Functions = inject(Functions);
  private store: Store = inject(Store);
  private rendererFactory = inject(RendererFactory2);
  private renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

  private userName = this.store.selectSnapshot(UserState.getCurrentUser).name;

  private orgLogo = this.store.selectSnapshot(
    (state) => (state.securePro as SecureProStateModel).org.logoUrl
  );

  private org = this.store.selectSnapshot(
    (state) => (state.securePro as SecureProStateModel).org
  );

  private getOptions(filename: string): IHTML2PDFOptions {
    return {
      margin: 0,
      filename,
      image: { type: "jpeg", quality: 0.3 },
      html2canvas: { scale: 6, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
  }

  async generate(elementId = "print-out", filename = "secure-compliance-document") {
    filename += ` - ${new Date().toLocaleDateString("en-US")}.pdf`;
    const element = document.getElementById(elementId);
    await html2pdf(element, this.getOptions(filename));
  }

  async generateCoverLetterAndReport(
    coverLetterType: CoverLetterType,
    entity: IEntity,
    elementId = "print-out"
  ) {
    await this.generateCoverLetter(coverLetterType, entity, elementId);

    let htmlContent =
      "<div style='page-break-after: always; margin-bottom: 20px;'></div>";

    htmlContent += await this.generateReport(entity, elementId, true);
    const element = document.getElementById(elementId);

    if (element) {
      element.insertAdjacentHTML("beforeend", htmlContent);
    }
  }

  async generateCoverLetter(
    coverLetterType: CoverLetterType,
    entity: IEntity | undefined,
    elementId = "print-out",
    customContentOverride: ICustomCoverLetterContent | null = null
  ) {
    const coverLetterData = this.coverLetterData(entity);
    let content = coverLetterData.customCoverLetterContent?.[coverLetterType];
    if (!content) {
      content = coverLetterDefaults[coverLetterType];
    }
    if (customContentOverride && customContentOverride[coverLetterType]) {
      content = customContentOverride[coverLetterType];
    }
    let stringContent = JSON.stringify(content)
      .replaceAll("{Company}", coverLetterData.companyName)
      .replaceAll("{Signer}", coverLetterData.userName)
      .replaceAll("{Date}", coverLetterData.currentDate);
    if (this.org?.name) {
      stringContent = stringContent.replaceAll("{Pro Org Name}", this.org.name);
    }
    content = JSON.parse(stringContent);
    const el = document.getElementById(elementId);
    if (!el) throw new Error("Element not found");
    el.style.setProperty("border", "none");
    this.renderer.addClass(el, "print-out");
    const quill = new Quill(el, {
      readOnly: true,
      modules: {
        toolbar: null,
      },
      theme: "snow",
    });
    quill.setContents({ ops: content } as DeltaStatic);

    let footer = coverLetterFooter.replaceAll(
      "{Company}",
      coverLetterData.companyName
    );
    // Don't add the logo and address to self bill
    if (coverLetterType === "SelfBill") {
      footer = footer.replaceAll("{logoAndAddress}", "");
    } else {
      footer = footer.replaceAll(
        "{logoAndAddress}",
        getLogoAndAddressHtml(coverLetterData)
      );
    }
    el.insertAdjacentHTML("beforeend", footer);
    let header = coverLetterHeader;
    if (coverLetterType === "Direct") {
      header = header.replaceAll(
        "{header}",
        getLogoAndAddressHtml(coverLetterData, "file")
      );
    } else {
      header = header.replaceAll("{header}", getLogo(coverLetterData, "org"));
    }
    el.insertAdjacentHTML("afterbegin", header);
  }

  async generateReport(
    entity: IEntity | undefined,
    elementId = "print-out",
    returnHtml = false
  ) {
    if (!entity) return null;

    try {
      const createHtmlReportCallable = httpsCallable<
        ICreateReportReq,
        ICreateReportRes
      >(this.functions, cloudFunctionNames.reviewReport.createHtmlReport);

      const sendReportResponse = await createHtmlReportCallable({
        entityId: entity.id,
        orgId: entity.managingOrgId,
        currentDate: this.currentDate,
      });

      if (!sendReportResponse.data.success) {
        throw new Error("Failed to generate report");
      }

      if (returnHtml) {
        return sendReportResponse.data.htmlTemplate;
      } else {
        const element = document.getElementById(elementId);
        if (element) {
          element.innerHTML = sendReportResponse.data.htmlTemplate;
        }
        return null;
      }
    } catch (error: unknown) {
      console.error(error);
      return null;
    }
  }

  async generateSnapshotReport(
    entityId: UUID,
    processId: UUID,
    elementId = "print-out",
    returnHtml = false
  ) {
    try {
      const createSnapshotHtmlReportCallable = httpsCallable<
        ICreateSnapshotReportReq,
        ICreateSnapshotReportRes
      >(this.functions, cloudFunctionNames.reviewReport.createSnapshotHtmlReport);

      const sendReportResponse = await createSnapshotHtmlReportCallable({
        entityId,
        processId,
        currentDate: this.currentDate,
      });

      if (!sendReportResponse.data.success) {
        throw new Error("Failed to generate report");
      }

      if (returnHtml) {
        return sendReportResponse.data.htmlTemplate;
      } else {
        const element = document.getElementById(elementId);
        if (element) {
          element.innerHTML = sendReportResponse.data.htmlTemplate;
        }
        return null;
      }
    } catch (error: unknown) {
      console.error(error);
      return null;
    }
  }

  /**
  * @deprecated since owner re-structuring into their own collection
  */
  async generateDeprecatedReport(
    entity: IDeprecatedEntity,
    elementId = "print-out",
    returnHtml = false
  ) {
    try {
      const generateReportCallable = httpsCallable<
        ICreateDeprecatedReportReq,
        ICreateDeprecatedReportRes
      >(this.functions, cloudFunctionNames.reviewReport.createDeprecatedHtmlReport);

      const sendReportResponse = await generateReportCallable({
        entityData: entity,
        currentDate: this.currentDate,
      });

      if (!sendReportResponse.data.success) {
        throw new Error("Failed to generate report");
      }

      if (returnHtml) {
        return sendReportResponse.data.htmlTemplate;
      } else {
        const element = document.getElementById(elementId);
        if (element) {
          element.innerHTML = sendReportResponse.data.htmlTemplate;
        }
        return null;
      }
    } catch (error: unknown) {
      console.error(error);
      return null;
    }
  }

  private get currentDate() {
    const date = new Date();
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  private coverLetterData(entity: IEntity | undefined): ICoverLetterData {
    return {
      companyName: entity?.legalName || "",
      orgLogo: this.orgLogo,
      userName: this.userName,
      currentDate: this.currentDate,
      customCoverLetterContent:
        this.org?.customEmailContent?.customCoverLetterContent,
    };
  }
}
